import { inject, Injectable, NgZone } from '@angular/core';

import { StateService } from '@uirouter/core';
import { ApiUtilsService } from 'src/app/api/services/api-utils.service';
import { Orientation } from 'src/app/api/services/presentation';
import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { TemplateApiService } from 'src/app/api/services/template-api.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { PRESENTATION_ACCESS } from 'src/app/auth/types/access-level';
import { ENTITY_TYPES } from 'src/app/auth/types/entity-type';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';
import { EmbeddingPresentationsModalComponent } from 'src/app/editor/components/embedding-presentations-modal/embedding-presentations-modal.component';
import { PresentationManagerService } from 'src/app/presentations/services/presentation-manager.service';
import { PresentationsService } from 'src/app/presentations/services/presentations.service';
import { FirstScheduleService } from 'src/app/schedules/services/first-schedule.service';
import { ScheduleSelectorService } from 'src/app/schedules/services/schedule-selector.service';
import { ScheduleService } from 'src/app/schedules/services/schedule.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { PromiseUtilsService } from 'src/app/shared/services/promise-utils.service';
import { BrandingService } from '../template-components/services/branding.service';
import { BlueprintService } from './blueprint.service';
import { HistoryEntry, HistoryService } from './history.service';
import { ReviewService } from './review.service';

@Injectable({
  providedIn: 'root',
})
export class TemplateEditorService {
  static readonly HTML_TEMPLATE_DOMAIN = 'https://widgets.risevision.com';
  static readonly REVISION_STATUS_PUBLISHED = 'Published';
  static readonly REVISION_STATUS_REVISED = 'Revised';
  static readonly REVISION_STATUS_PENDING_REVIEW = 'Pending Review';
  static readonly HTML_PRESENTATION_TYPE = 'HTML Template';
  static readonly IGNORED_FIELDS_HTML_PRESENTATION = [
    'id',
    'companyId',
    'revisionStatus',
    'revisionStatusName',
    'reviewRequestedBy',
    'changeDate',
    'changedBy',
    'creationDate',
    'publish',
  ];
  static readonly IGNORED_FIELDS = [
    ...TemplateEditorService.IGNORED_FIELDS_HTML_PRESENTATION,
    'layout',
  ];

  hasUnsavedChanges = false;
  presentation: any = {};
  currentSequenceId: number = 0;
  private _lastSavedSequenceId: number = 0;
  private _isSaveFromRestoration = false;
  // Id of a presentation added in this session that has not been published yet
  private _newPresentationId: string | undefined;
  loadingPresentation;
  savingPresentation;
  errorMessage;
  apiError;

  aiEditorActive = false;
  isInlineEditing = false;

  private historyService = inject(HistoryService);
  private reviewService = inject(ReviewService);
  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    private ngZone: NgZone,
    private stateService: StateService,
    private broadcaster: BroadcasterService,
    private modalService: ModalService,
    private presentationApiService: PresentationApiService,
    private presentationsService: PresentationsService,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private apiUtils: ApiUtilsService,
    private userStateService: UserStateService,
    private permissionService: PermissionService,
    private firstScheduleService: FirstScheduleService,
    private brandingFactory: BrandingService,
    private blueprintFactory: BlueprintService,
    private scheduleService: ScheduleService,
    private trackerService: TrackerService,
    private scheduleSelectorService: ScheduleSelectorService,
    private promiseUtils: PromiseUtilsService,
    private presentationManager: PresentationManagerService,
    private featuresService: FeaturesService,
    private templateApiService: TemplateApiService,
  ) {
    this.processErrorCode = processErrorCodeAdapter.createCallable();
    this._init();
  }

  canEditPresentation() {
    return (
      this.permissionService.hasAccess(
        PRESENTATION_ACCESS.EDITOR,
        this.presentation?.id,
      ) ||
      this.userStateService.isAssignedPublisher() ||
      this.userStateService.isModerator()
    );
  }

  canPublishPresentation() {
    return this.permissionService.hasAccess(
      PRESENTATION_ACCESS.PUBLISHER,
      this.presentation?.id,
    );
  }

  canSendPresentationForReview() {
    return (
      this.permissionService.getRoleAccessLevel(ENTITY_TYPES.PRESENTATION) ===
        PRESENTATION_ACCESS.EDITOR &&
      !this.userStateService.isAssignedPublisher()
    );
  }

  _parseJSON(json) {
    try {
      return JSON.parse(json);
    } catch (err) {
      console.error('Invalid JSON: ' + err);
      return null;
    }
  }

  _setPresentation(presentation, isUpdate?) {
    if (isUpdate) {
      this.presentation.id = presentation.id;
      this.presentation.companyId = presentation.companyId;
      this.presentation.revisionStatus = presentation.revisionStatus;
      this.presentation.revisionStatusName = presentation.revisionStatusName;
      this.presentation.credentials = presentation.credentials;
      this.presentation.creationDate = presentation.creationDate;
      this.presentation.changeDate = presentation.changeDate;
      this.presentation.changedBy = presentation.changedBy;
    } else {
      // PresentationManagerService already handles JSON parsing,
      // but preserve backward compatibility for direct API calls
      if (typeof presentation.templateAttributeData === 'string') {
        presentation.templateAttributeData =
          this._parseJSON(presentation.templateAttributeData) || {};
      } else if (!presentation.templateAttributeData) {
        presentation.templateAttributeData = {};
      }

      this.presentation = presentation;
    }

    // Clear Web Page Authentication fields
    delete this.presentation.credentialsUpdate;

    this.broadcaster.emit('presentationUpdated');
  }

  _getPresentationForUpdate() {
    var presentationVal = JSON.parse(JSON.stringify(this.presentation));
    presentationVal.templateAttributeData =
      presentationVal.templateAttributeData || {};

    // Temporary fix for existing layout based templates. Will remove soon.
    if (presentationVal.layout) {
      presentationVal.layout = presentationVal.layout.replace(
        /\s*data-frame-src="[^"]*"/gi,
        '',
      );
    }

    // Fixes the AI layout orientation so the Viewer scales it to fit.
    if (
      presentationVal.layout &&
      !presentationVal.templateAttributeData.orientationPreset
    ) {
      Object.assign(
        presentationVal.templateAttributeData,
        this.blueprintFactory.getOrientationPresetAttributeData(
          presentationVal.templateAttributeData.orientation ?? 'landscape',
        ),
      );
    }

    this.blueprintFactory.updateOrientation(
      presentationVal.templateAttributeData,
    );

    presentationVal.templateAttributeData = JSON.stringify(
      presentationVal.templateAttributeData,
    );

    if (this.presentation.credentialsUpdate) {
      presentationVal.credentialsUpdate = JSON.stringify(
        this.presentation.credentialsUpdate,
      );
    }

    return presentationVal;
  }

  addFromProduct(
    productDetails,
    trackingData?: any,
    folderId?: string,
    previewOnly?: boolean,
    templateAttributeData?: any,
  ) {
    this._clearMessages();

    this.presentation = {
      id: undefined,
      productCode: productDetails.productCode,
      name: productDetails.name,
      presentationType: TemplateEditorService.HTML_PRESENTATION_TYPE,
      templateAttributeData: templateAttributeData || {},
      revisionStatusName: undefined,
      isTemplate: false,
      folderId,
    };

    this.trackerService.presentationEvent(
      'HTML Template Copied',
      productDetails.productCode,
      productDetails.name,
      trackingData,
    );

    return this.blueprintFactory
      .getBlueprintCached(this.presentation.productCode)
      .then(() => {
        if (!previewOnly) {
          return this.save();
        }
      })
      .then(null, (e) => {
        this._showErrorMessage('add', e);
        return Promise.reject(e);
      });
  }

  addFromLayout(
    name: string,
    layout: string,
    trackingData?: any,
    folderId?: string,
    brandingOverride?: { baseColor: string; accentColor: string },
    orientation?: Orientation,
  ) {
    this._clearMessages();

    this.presentation = {
      id: undefined,
      name: name,
      layout: layout,
      presentationType: TemplateEditorService.HTML_PRESENTATION_TYPE,
      templateAttributeData: {},
      revisionStatusName: undefined,
      isTemplate: false,
      folderId,
    };

    this.trackerService.presentationEvent(
      'Presentation Created From Layout',
      undefined,
      name,
      trackingData,
    );

    return this.blueprintFactory
      .generateBlueprintDataFromLayout(layout, true, orientation)
      .then((blueprint) => {
        this.presentation.templateAttributeData =
          this.blueprintFactory.buildAttributeData(blueprint);
        if (brandingOverride) {
          this.presentation.templateAttributeData.brandingOverride =
            brandingOverride;
        }
        return this.save();
      })
      .then(null, (e) => {
        this._showErrorMessage('add', e);
        return Promise.reject(e);
      });
  }

  addPresentation() {
    var presentationVal = this._getPresentationForUpdate();

    return this.presentationApiService.add(presentationVal).then((resp) => {
      if (resp && resp.item && resp.item.id) {
        this._newPresentationId = resp.item.id;

        this.trackerService.presentationEvent(
          'Presentation Created',
          resp.item.id,
          resp.item.name,
          {
            presentationType: 'HTML Template',
            sharedTemplate: resp.item.productCode,
          },
        );

        const routePromise = !this.aiEditorActive
          ? this.stateService.go(
              'apps.editor.templates.edit',
              {
                presentationId: resp.item.id,
                productId: undefined,
              },
              {
                location: 'replace',
              },
            )
          : Promise.resolve();

        console.log('Presentation created:', resp);

        return routePromise
          .catch(() => undefined)
          .then(() => {
            if (this.presentation?.id !== resp.item.id) {
              this._setPresentation({
                ...this.presentation,
                ...resp.item,
              });
            }

            this.broadcaster.emit('presentationCreated');

            return resp.item.id;
          });
      }
    });
  }

  copyPresentation() {
    this.trackerService.presentationEvent(
      (this.presentation.isTemplate ? 'Template' : 'Presentation') + ' Copied',
      this.presentation.id,
      this.presentation.name,
      {
        presentationType: this.presentation.presentationType,
      },
    );

    this.presentation.id = undefined;
    this.presentation.name = 'Copy of ' + this.presentation.name;
    this.presentation.revisionStatusName = undefined;
    this.presentation.isTemplate = false;
    this._resetHistoryBaseline();
    this.broadcaster.emit('risevision.template-editor.presentationLoaded');

    // Layout-based presentations (AI-generated) have no blueprint to fetch.
    return (
      this.presentation.layout
        ? Promise.resolve()
        : this.blueprintFactory.getBlueprintCached(
            this.presentation.productCode,
          )
    )
      .then(() => {
        return this.save();
      })
      .then((presentationId) => {
        this._resetHistoryBaseline();
        this.broadcaster.emit('risevision.template-editor.presentationLoaded');
        return presentationId;
      })
      .then(null, (e) => {
        this._showErrorMessage('copy', e);
        return Promise.reject(e);
      });
  }

  copySharedPresentation(presentationId, folderId = null) {
    return this.getPresentation(presentationId).then(() => {
      if (folderId) {
        this.setPresentationFolder(folderId);
      }
      return this.copyPresentation();
    });
  }

  private setPresentationFolder(folderId: string) {
    this.presentation.folderId = folderId;
  }

  updatePresentation() {
    if (!this.hasUnsavedChanges) {
      //Factory has no Changes.
      return Promise.resolve();
    }

    var presentationVal = this._getPresentationForUpdate();

    return this.presentationApiService
      .update(presentationVal.id, presentationVal)
      .then((resp) => {
        this.trackerService.presentationEvent(
          'Presentation Updated',
          resp.item.id,
          resp.item.name,
        );

        this._setPresentation(resp.item, true);
        this.presentationsService.resetPresentation(resp.item);

        return Promise.resolve(resp.item.id);
      });
  }

  isUnsaved(): boolean {
    return this.currentSequenceId !== this._lastSavedSequenceId;
  }

  snapshotHistory(): void {
    if (this.historyService.isRestoring) {
      return;
    }
    this.historyService.push(this.presentation, this._getCleanBranding());
    this.currentSequenceId = this.historyService.getCurrentSequenceId();
    if (this.currentSequenceId !== this._lastSavedSequenceId) {
      this.hasUnsavedChanges = true;
    }
  }

  private _resetHistoryBaseline(): void {
    this.historyService.reset();
    this.historyService.push(this.presentation, this._getCleanBranding());
    this.currentSequenceId = this.historyService.getCurrentSequenceId();
    this._lastSavedSequenceId = this.currentSequenceId;
  }

  private _getCleanBranding(): any {
    const { logoFileMetadata, ...clean } =
      this.brandingFactory.brandingSettings || {};
    return clean;
  }

  canUndo(): boolean {
    return this.historyService.canUndo();
  }

  canRedo(): boolean {
    return this.historyService.canRedo();
  }

  undo(): boolean {
    const entry = this.historyService.undo();
    if (!entry) {
      return false;
    }
    this._restoreHistoryEntry(entry);
    return true;
  }

  redo(): boolean {
    const entry = this.historyService.redo();
    if (!entry) {
      return false;
    }
    this._restoreHistoryEntry(entry);
    return true;
  }

  private _restoreHistoryEntry(entry: HistoryEntry): void {
    this._isSaveFromRestoration = true;
    this.historyService.isRestoring = true;
    try {
      this.presentation = entry.presentation;
      this.brandingFactory.restoreBrandingSettings(entry.branding);
      this.hasUnsavedChanges = true;
      this.currentSequenceId = entry.sequenceId;
      this.broadcaster.emit('risevision.template-editor.historyRestored');
    } finally {
      this.historyService.isRestoring = false;
    }
  }

  save() {
    var deferred = this.promiseUtils.generateDeferredPromise(),
      saveFunction;

    const isExistingPresentation = !!this.presentation.id;

    if (isExistingPresentation) {
      saveFunction = this.updatePresentation.bind(this);
    } else {
      saveFunction = this.addPresentation.bind(this);
    }

    this._clearMessages();

    //show spinner
    this.loadingPresentation = true;
    this.savingPresentation = true;

    const savedSequenceId = this.currentSequenceId;

    Promise.all([this.brandingFactory.saveBranding(), saveFunction()])
      .then(([_, saveResp]) => {
        this._lastSavedSequenceId = isExistingPresentation
          ? savedSequenceId
          : this.currentSequenceId;

        deferred.resolve(saveResp);
      })
      .then(null, (e) => {
        // If adding, and there is a Presentation Id it means save was successful
        // and the failure was to update Branding
        this._showErrorMessage(this.presentation.id ? 'update' : 'add', e);

        deferred.reject(e);
      })
      .finally(() => {
        this._isSaveFromRestoration = false;
        this.loadingPresentation = false;
        this.savingPresentation = false;

        this.ngZone.run(() => {});
      });

    return deferred.promise;
  }

  getPresentation(presentationId?) {
    var deferred = this.promiseUtils.generateDeferredPromise();

    this._clearMessages();

    //show loading spinner
    this.loadingPresentation = true;

    this.presentationManager
      .loadPresentation(presentationId)
      .then((result) => {
        if (result.success && result.data?.presentation) {
          this._setPresentation(result.data.presentation);
          this.presentation = JSON.parse(JSON.stringify(this.presentation));
          // Blueprint data is now loaded by PresentationManagerService
          // but we still need to ensure blueprintFactory has the data
          if (result.data.blueprintData) {
            this.blueprintFactory.blueprintData = result.data.blueprintData;
          }

          this.brandingFactory._loadBranding();
          this._resetHistoryBaseline();
          this.broadcaster.emit(
            'risevision.template-editor.presentationLoaded',
          );

          const presentationId = this.presentation?.id;
          if (presentationId) {
            return this.permissionService
              .loadShareAccess(ENTITY_TYPES.PRESENTATION, presentationId)
              .catch(() => undefined)
              .then(() => deferred.resolve());
          }

          deferred.resolve();
        } else {
          throw result.error || new Error('Failed to load presentation data');
        }
      })
      .catch((e) => {
        this._showErrorMessage('get', e);
        this.presentation = {};
        this.blueprintFactory.blueprintData = null;

        deferred.reject(e);
      })
      .finally(() => {
        this.loadingPresentation = false;
      });

    return deferred.promise;
  }

  deletePresentation() {
    var deferred = this.promiseUtils.generateDeferredPromise();

    this._clearMessages();

    //show spinner
    this.loadingPresentation = true;
    this.savingPresentation = true;

    this.presentationApiService
      .delete(this.presentation.id)
      .then(() => {
        this.trackerService.presentationEvent(
          'Presentation Deleted',
          this.presentation.id,
          this.presentation.name,
        );

        this.broadcaster.emit('presentationDeleted');

        this.presentation = {};

        this.stateService.go('apps.editor.list');
        deferred.resolve();
      })
      .then(null, (e) => {
        let embeddingPromise: Promise<any> = Promise.reject(e);
        let error = this.apiUtils.getError(e);
        let msg = error.message || '';
        if (
          msg.includes('used in one or more schedules') ||
          msg.includes('embedded in one or more presentations')
        ) {
          embeddingPromise = this._showEmbeddingItems();
          this._setErrorMessage('delete', e);
        }

        // Handle remaining presentation.delete errors or any error generated while loading embedding schedules/presentations
        embeddingPromise
          .catch((e) => {
            this._showErrorMessage('delete', e);
          })
          .finally(() => {
            deferred.reject(e);
          });
      })
      .finally(() => {
        this.loadingPresentation = false;
        this.savingPresentation = false;

        this.ngZone.run(() => {});
      });

    return deferred.promise;
  }

  _showEmbeddingItems() {
    let presentationsPromise =
      this.presentationApiService.listEmbeddingPresentations(
        this.presentation.id,
      );
    let schedulesPromise = this.presentationApiService.listEmbeddingSchedules(
      this.presentation.id,
    );

    return Promise.all([presentationsPromise, schedulesPromise]).then(
      ([presentations, schedules]) => {
        return this.modalService
          .showMediumDialog(EmbeddingPresentationsModalComponent, {
            presentation: this.presentation,
            embeddingPresentations: presentations.items || [],
            embeddingSchedules: schedules.items || [],
          })
          .catch(() => {}); // Do not trigger the fallback modal
      },
    );
  }

  openReviewRequest() {
    if (
      !this.featuresService.isFeatureAvailable(
        FeaturesService.FEATURE_PUBLICATION_APPROVAL,
      )
    ) {
      this.featuresService.showUpgradePlanModal(
        FeaturesService.FEATURE_PUBLICATION_APPROVAL,
      );
      return;
    }
    if (!this.presentation.id) {
      return Promise.reject(new Error('Presentation ID is required'));
    }

    this.trackerService.presentationEvent(
      'Presentation Review Requested',
      this.presentation.id,
      this.presentation.name,
    );

    return this.reviewService
      .requestPublish(this.presentation.id)
      .then((result) => {
        if (result?.item) {
          this._setPresentation(result.item);
        }
        return result;
      });
  }

  openRequestChanges() {
    if (
      !this.featuresService.isFeatureAvailable(
        FeaturesService.FEATURE_PUBLICATION_APPROVAL,
      )
    ) {
      this.featuresService.showUpgradePlanModal(
        FeaturesService.FEATURE_PUBLICATION_APPROVAL,
      );
      return;
    }
    if (!this.presentation.id) {
      return Promise.reject(new Error('Presentation ID is required'));
    }

    this.trackerService.presentationEvent(
      'Presentation Changes Requested',
      this.presentation.id,
      this.presentation.name,
    );

    return this.reviewService
      .requestChanges(this.presentation)
      .then((result) => {
        if (result?.item) {
          this.presentation.revisionStatusName = 'Revised';
          this.presentation.revisionStatus = 1;
        }
      });
  }

  isRevised() {
    return (
      this.presentation.revisionStatusName ===
      TemplateEditorService.REVISION_STATUS_REVISED
    );
  }

  isPendingReview() {
    return (
      this.presentation.revisionStatusName ===
      TemplateEditorService.REVISION_STATUS_PENDING_REVIEW
    );
  }

  isPublishDisabled() {
    // A presentation that was just added has nothing to publish yet, but
    // Publish is still how it gets assigned to a Schedule.
    const isNotRevised =
      !this.isRevised() &&
      !this.brandingFactory.isRevised() &&
      !this._isNewUnpublishedPresentation() &&
      this.scheduleService.hasSchedules();

    return (
      (this.savingPresentation || this.isUnsaved() || isNotRevised) &&
      !this.isPendingReview()
    );
  }

  private _isNewUnpublishedPresentation(): boolean {
    return (
      !!this._newPresentationId &&
      this.presentation.id === this._newPresentationId
    );
  }

  publish() {
    return this._publish().then(() => {
      return this.scheduleSelectorService.checkAssignedToSchedules();
    });
  }

  _publish() {
    var deferred = this.promiseUtils.generateDeferredPromise();

    this._clearMessages();

    //show spinner
    this.loadingPresentation = true;
    this.savingPresentation = true;

    var tasks = [this._publishPresentation()];
    if (!this.userStateService.isAssignedPublisher()) {
      tasks.push(this.brandingFactory.publishBranding());
    }

    Promise.all(tasks)
      .then(() => {
        deferred.resolve();
      })
      .then(null, (e) => {
        this._showErrorMessage('publish', e);

        deferred.reject();
      })
      .finally(() => {
        this.loadingPresentation = false;
        this.savingPresentation = false;

        this.ngZone.run(() => {});
      });

    return deferred.promise;
  }

  _publishPresentation() {
    if (!this.isRevised() && !this.isPendingReview()) {
      // template is already published
      return this._createFirstSchedule();
    }

    return this.presentationApiService
      .publish(this.presentation.id)
      .then(() => {
        this.trackerService.presentationEvent(
          'Presentation Published',
          this.presentation.id,
          this.presentation.name,
          {
            revisionStatus: this.presentation.revisionStatus,
          },
        );

        this.presentation.revisionStatusName =
          TemplateEditorService.REVISION_STATUS_PUBLISHED;
        this.presentation.changeDate = new Date();
        this.presentation.changedBy = this.userStateService.getUsername();
        this._newPresentationId = undefined;

        this.presentationsService.resetPresentation(this.presentation);
        this.broadcaster.emit('presentationPublished');

        return this._createFirstSchedule();
      });
  }

  _createFirstSchedule() {
    // Assigned Publishers (teachers) cannot create Schedules, and Core rejects
    // the request with a 403 when they have no assigned Schedules
    if (this.userStateService.isAssignedPublisher()) {
      return Promise.resolve();
    }

    return this.firstScheduleService
      .create(this.presentation)
      .then(() => {
        return this.scheduleSelectorService.loadSelectedSchedules();
      })
      .catch((err) => {
        return err === 'Already have Schedules'
          ? Promise.resolve()
          : Promise.reject(err);
      });
  }

  _setErrorMessage(action, e) {
    this.errorMessage = 'Failed to ' + action + ' Presentation.';
    this.apiError = this.processErrorCode('Presentation', action, e);

    console.error(this.errorMessage, e);
  }

  _showErrorMessage(action, e) {
    this._setErrorMessage(action, e);

    // Check if the error is due to widgets.risevision.com being blocked
    if (
      e &&
      e.url &&
      e.url.includes('widgets.risevision.com') &&
      e.status === 0
    ) {
      this.errorMessage = 'Unable to access Rise Vision widgets.';
      this.apiError =
        'Unable to access widgets.risevision.com, please check your network configuration and if necessary whitelist the domain.';
    }

    this.modalService.showMessage(this.errorMessage, this.apiError);
  }

  _clearMessages() {
    this.loadingPresentation = false;
    this.savingPresentation = false;

    this.errorMessage = '';
    this.apiError = '';
  }

  async checkIfPresentationIsInteractive() {
    const productCode = this.presentation?.productCode;
    if (productCode) {
      try {
        const templates = await this.templateApiService.listTemplates({
          filter: 'templateCategories:=Interactive',
        });

        const isInteractive = templates.items.find(
          (template) => template.productCode === productCode,
        );

        if (isInteractive) {
          this.broadcaster.emit(FeaturesService.FEATURE_INTERACTIVE_DISPLAYS);
        }
      } catch (error) {
        console.error('Failed to list templates:', error);
      }
    }
  }

  teardown(): void {
    this.presentationManager.clearPresentation(this.presentation.id);
    this.historyService.reset();
    this.currentSequenceId = 0;
    this._lastSavedSequenceId = 0;
    this.hasUnsavedChanges = false;
    this.isInlineEditing = false;
    this._newPresentationId = undefined;
    this.presentation = {};
    this._clearMessages();
  }

  _init() {
    this.presentation = {};

    this._clearMessages();
  }
}
